/**
 * Subscription Service
 * Handles API calls for subscription management and usage tracking.
 * Uses the shared apiClient (Axios) for auth, CSRF, and credentials.
 */

import apiClient from './apiClient';

class SubscriptionService {
  async _request(endpoint) {
    // Subscription endpoints are never cached; append a cache-buster
    const { data } = await apiClient.get(endpoint, {
      params: { t: Date.now() },
      headers: { 'Cache-Control': 'no-store' },
    });
    // Normalise to { success, data } shape
    return {
      success: data.success !== undefined ? data.success : true,
      data: data.data ?? data,
    };
  }

  async getCurrentSubscription() {
    try {
      return await this._request('/api/subscription/current');
    } catch (error) {
      console.error('[SubscriptionService] getCurrentSubscription failed:', error);
      return { success: false, data: null, error: error.message };
    }
  }

  async getCurrentUsage() {
    try {
      return await this._request('/api/subscription/usage');
    } catch (error) {
      console.error('[SubscriptionService] getCurrentUsage failed:', error);
      return {
        success: false,
        data: {
          total_itineraries: 0,
          monthly_itineraries: 0,
          monthly_limit: 5,
          annual_limit: 60,
          plan_name: 'free',
          can_create: true,
          usage_percentage: 0,
        },
        error: error.message,
      };
    }
  }

  async canCreateItinerary() {
    try {
      return await this._request('/api/subscription/can-create-itinerary');
    } catch (error) {
      console.error('[SubscriptionService] canCreateItinerary failed:', error);
      return { success: false, data: { canCreate: true }, error: error.message };
    }
  }

  async getUpgradeRecommendations() {
    try {
      return await this._request('/api/subscription/upgrade-recommendations');
    } catch (error) {
      console.error('[SubscriptionService] getUpgradeRecommendations failed:', error);
      return {
        success: false,
        data: { should_upgrade: false, current_plan: 'free', usage_percentage: 0 },
        error: error.message,
      };
    }
  }
}

export default new SubscriptionService();
